// src/pages/Dashboard.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, getDocs, orderBy,
  limit, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/* ── Design tokens (mirrors faircast-rn/src/theme.ts) ─────────────────────── */
const C = {
  coral:       'var(--primary-color)',
  coralDim:    'var(--primary-dim)',
  bg:          'var(--bg-dark)',
  surface1:    'var(--bg-card)',
  surface2:    'var(--bg-elevated)',
  surface3:    'var(--bg-surface3)',
  border:      'var(--border-color)',
  borderFaint: 'var(--text-faint, #594139)',
  text:        'var(--text-primary)',
  muted:       'var(--text-secondary)',
  faint:       'var(--text-mute)',
};

const MEDALS      = ['🥇', '🥈', '🥉'];
const SEG_COLORS  = ['#FF6B35', '#FF9163', '#E55520', '#FFB590', '#CC4010', '#555'];
const CREATOR_POT = 24; // 30 × 80%
const TOP_N       = 5;

function formatSeconds(s) {
  if (!s) return '0 min';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} t ${m} min` : `${m} min`;
}

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

function getInitials(username, email) {
  const name = username ?? email ?? '?';
  const parts = name.split(/[\s@._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

/* ── Donut chart (SVG — mirrors DonutChart.tsx) ────────────────────────────── */
const SIZE        = 144;
const CX          = SIZE / 2;
const CY          = SIZE / 2;
const R           = 52;
const SW          = 18;
const CIRC        = 2 * Math.PI * R;
const GAP         = (3 / 360) * CIRC;

function DonutChart({ segments, totalDkk }) {
  let angle = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <svg width={SIZE} height={SIZE} style={{ flexShrink: 0 }}>
        {segments.length === 0 ? (
          <circle cx={CX} cy={CY} r={R} fill="none" stroke={C.surface3} strokeWidth={SW} />
        ) : (
          segments.map(seg => {
            const dash   = Math.max(0, seg.pct * CIRC - GAP);
            const rotate = angle - 90;
            angle       += seg.pct * 360;
            return (
              <circle
                key={seg.id}
                cx={CX} cy={CY} r={R}
                fill="none"
                stroke={seg.color}
                strokeWidth={SW}
                strokeDasharray={`${dash} ${CIRC - dash}`}
                strokeDashoffset={0}
                transform={`rotate(${rotate} ${CX} ${CY})`}
              />
            );
          })
        )}
        <text x={CX} y={CY - 7} textAnchor="middle" fill={C.coral}
          style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 800 }}>
          {totalDkk.toFixed(0)}
        </text>
        <text x={CX} y={CY + 12} textAnchor="middle" fill={C.faint}
          style={{ fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 1.5 }}>
          KR.
        </text>
      </svg>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {segments.map(seg => (
          <div key={seg.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: seg.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {seg.label}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.coral, fontFamily: "'JetBrains Mono', monospace", minWidth: 52, textAlign: 'right' }}>
              {seg.dkk.toFixed(2)} kr
            </span>
            <span style={{ fontSize: 11, color: C.faint, minWidth: 32, textAlign: 'right' }}>
              {Math.round(seg.pct * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Section heading (matches RN sectionHeading style) ─────────────────────── */
function SectionHeading({ children, style }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: C.muted,
      marginBottom: 10,
      marginTop: 4,
      ...style,
    }}>{children}</div>
  );
}

/* ── Card wrapper ───────────────────────────────────────────────────────────── */
function Card({ children, style }) {
  return (
    <div style={{
      background: C.surface1,
      borderRadius: 16,
      border: `1px solid ${C.border}`,
      overflow: 'hidden',
      marginBottom: 20,
      ...style,
    }}>{children}</div>
  );
}

/* ── Row inside a card ──────────────────────────────────────────────────────── */
function CardRow({ children, last }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '14px 16px',
      gap: 12,
      borderBottom: last ? 'none' : `1px solid ${C.border}`,
    }}>{children}</div>
  );
}

/* ── Avatar circle ──────────────────────────────────────────────────────────── */
function Avatar({ initials, size = 64 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: 'linear-gradient(135deg, #FF6B35 0%, #1a1a2e 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <span style={{
        fontSize: size * 0.35,
        fontWeight: 800,
        color: '#fff',
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: 1,
      }}>{initials}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function Dashboard() {
  const { currentUser, userProfile, verifyEmail } = useAuth();

  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error
  const [timeFrame, setTimeFrame] = useState('month');
  const [sessions, setSessions] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [podcastNames, setPodcastNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      // ── Listening history ──────────────────────────────────────────
      let hist = [];
      try {
        const q = query(
          collection(db, 'listening_sessions'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(20),
        );
        const snap = await getDocs(q);
        hist = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch { /* new user — no sessions yet */ }
      setSessions(hist);

      // ── Monthly summary (DKK donut) ────────────────────────────────
      const monthKey = new Date().toISOString().slice(0, 7); // "YYYY-MM"
      try {
        const sumSnap = await getDoc(
          doc(db, 'users', currentUser.uid, 'monthly_summaries', monthKey),
        );
        if (sumSnap.exists()) setMonthlySummary(sumSnap.data());
      } catch { /* non-fatal */ }

      // ── Podcast names for donut legend ────────────────────────────
      const podcastIds = new Set([
        ...hist.map(s => s.podcastId).filter(Boolean),
        ...Object.keys(monthlySummary?.podcastTimes ?? {}),
      ]);
      if (podcastIds.size > 0) {
        const pairs = await Promise.all(
          [...podcastIds].map(async id => {
            try {
              const p = await getDoc(doc(db, 'podcasts', id));
              return [id, p.exists() ? (p.data().title ?? id) : id];
            } catch { return [id, id]; }
          }),
        );
        setPodcastNames(Object.fromEntries(pairs));
      }

      setError('');
    } catch (err) {
      console.error(err);
      setError('Kunne ikke hente dine data.');
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  /* ── Derived data ───────────────────────────────────────────────────────── */
  const totalTime  = monthlySummary?.totalValidatedTime ?? 0;
  const epCount    = monthlySummary?.episodeCount ?? sessions.length;
  const podTimes   = monthlySummary?.podcastTimes ?? {};

  const breakdown  = Object.entries(podTimes).sort(([, a], [, b]) => b - a);
  const topEntries = breakdown.slice(0, TOP_N);
  const othersSec  = breakdown.slice(TOP_N).reduce((s, [, v]) => s + v, 0);

  const segments = [
    ...topEntries.map(([id, secs], i) => ({
      id,
      label: podcastNames[id] ?? id,
      pct:   totalTime > 0 ? secs / totalTime : 0,
      dkk:   totalTime > 0 ? CREATOR_POT * (secs / totalTime) : 0,
      color: SEG_COLORS[i] ?? SEG_COLORS[0],
    })),
    ...(othersSec > 0 ? [{
      id: 'others', label: 'Øvrige',
      pct:  totalTime > 0 ? othersSec / totalTime : 0,
      dkk:  totalTime > 0 ? CREATOR_POT * (othersSec / totalTime) : 0,
      color: SEG_COLORS[5],
    }] : []),
  ];

  const top3 = breakdown.slice(0, 3);

  const handleResendVerification = async () => {
    setResendState('sending');
    try {
      await verifyEmail();
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  };

  const initials  = getInitials(userProfile?.username, currentUser?.email);
  const isActive  = userProfile?.isAdmin || userProfile?.isCreator || userProfile?.subscriptionStatus === 'active';
  const monthLabel = new Date().toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 20px 80px', maxWidth: 720, margin: '0 auto' }}>

      {/* ── Avatar + name ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <Avatar initials={initials} size={64} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: C.text, marginBottom: 2 }}>
            {userProfile?.username ?? currentUser?.email?.split('@')[0] ?? '—'}
          </div>
          {userProfile?.username && (
            <div style={{ fontSize: 12, color: C.faint, marginBottom: 6 }}>{currentUser?.email}</div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Email verified badge */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 9999,
              background: C.surface2, border: `1px solid ${currentUser?.emailVerified ? '#166534' : '#7f1d1d'}`,
              fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
              color: currentUser?.emailVerified ? '#4ade80' : '#f87171',
            }}>
              {currentUser?.emailVerified ? '✓ Bekræftet' : '⚠ Ikke bekræftet'}
            </span>
            {!currentUser?.emailVerified && (
              resendState === 'sent' ? (
                <span style={{ fontSize: 11, color: '#4ade80' }}>E-mail sendt ✓</span>
              ) : (
                <button
                  onClick={handleResendVerification}
                  disabled={resendState === 'sending'}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    fontSize: 11, color: resendState === 'error' ? '#f87171' : C.faint,
                    cursor: 'pointer', textDecoration: 'underline',
                    opacity: resendState === 'sending' ? 0.5 : 1,
                  }}
                >
                  {resendState === 'error' ? 'Fejl — prøv igen' : resendState === 'sending' ? 'Sender…' : 'Gensend bekræftelses-e-mail'}
                </button>
              )
            )}
            {/* Subscription badge */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 9999,
              background: C.surface2, border: `1px solid ${isActive ? '#166534' : '#7f1d1d'}`,
              fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
              color: isActive ? '#4ade80' : '#f87171',
            }}>
              {isActive ? '✓ Aktiv' : '✗ Inaktiv'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Time frame toggle ─────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        background: C.surface1,
        border: `1px solid ${C.border}`,
        borderRadius: 9999,
        padding: 3,
        marginBottom: 24,
      }}>
        {[
          { key: 'month',   label: 'Denne måned' },
          { key: 'alltime', label: 'Alt tid' },
        ].map(tf => (
          <button key={tf.key} onClick={() => setTimeFrame(tf.key)} style={{
            flex: 1,
            padding: '7px 0',
            borderRadius: 9999,
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            transition: 'all 0.15s',
            background: timeFrame === tf.key ? C.coral : 'transparent',
            color: timeFrame === tf.key ? '#fff' : C.muted,
          }}>{tf.label}</button>
        ))}
      </div>

      {/* ── Stat cards ────────────────────────────────────────────── */}
      <SectionHeading>
        {timeFrame === 'month'
          ? monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
          : 'Samlet lytning'}
      </SectionHeading>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Lyttetid',   value: formatSeconds(totalTime), mono: true },
          { label: 'Episoder',   value: String(epCount),          mono: true },
        ].map(stat => (
          <div key={stat.label} style={{
            background: C.surface1,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '16px 20px',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 24,
              fontWeight: 800,
              color: C.coral,
              letterSpacing: -1,
              marginBottom: 4,
            }}>{loading ? '—' : stat.value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── DKK donut ─────────────────────────────────────────────── */}
      {timeFrame === 'month' && (
        <>
          <SectionHeading>Din støtte denne måned</SectionHeading>
          <Card>
            <div style={{ padding: 20 }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: C.faint }}>Indlæser…</div>
              ) : segments.length > 0 ? (
                <DonutChart segments={segments} totalDkk={CREATOR_POT} />
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🎧</div>
                  <div style={{ fontSize: 14, color: C.muted }}>Lyt til podcasts for at se din støtte.</div>
                </div>
              )}
              <div style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${C.border}`,
                fontSize: 12,
                color: C.faint,
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span>80% af 30 kr = 24,00 kr til skaberne</span>
                <span style={{ color: C.coral, fontWeight: 700 }}>24,00 kr</span>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ── Top 3 ─────────────────────────────────────────────────── */}
      {top3.length > 0 && (
        <>
          <SectionHeading>Dine top podcasts</SectionHeading>
          <Card>
            {top3.map(([id, secs], i) => (
              <CardRow key={id} last={i === top3.length - 1}>
                <span style={{ fontSize: 22, width: 30, textAlign: 'center', flexShrink: 0 }}>{MEDALS[i]}</span>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: 18, fontWeight: 700, color: C.text,
                }}>
                  {(podcastNames[id] ?? id).charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {podcastNames[id] ?? id}
                  </div>
                  <div style={{ fontSize: 12, color: C.coral, marginTop: 2 }}>{formatSeconds(secs)}</div>
                </div>
              </CardRow>
            ))}
          </Card>
        </>
      )}

      {/* ── Session history ───────────────────────────────────────── */}
      <SectionHeading>Seneste lytning</SectionHeading>

      {loading ? (
        <Card><div style={{ padding: '24px', textAlign: 'center', color: C.faint }}>Indlæser…</div></Card>
      ) : error ? (
        <Card><div style={{ padding: '16px', color: '#f87171', fontSize: 14 }}>{error}</div></Card>
      ) : sessions.length > 0 ? (
        <Card>
          {sessions.map((s, i) => {
            const progress = Math.min(100, Math.round((s.progress ?? s.completionPercentage ?? 0) * 100));
            const isPS = s.contentType === 'public_service';
            return (
              <div key={s.id} style={{
                padding: '14px 16px',
                borderBottom: i < sessions.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      {s.episodeTitle || 'Ukendt episode'}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      {s.podcastTitle || podcastNames[s.podcastId] || 'Ukendt podcast'}
                      {isPS && (
                        <span style={{ marginLeft: 6, color: '#60a5fa', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                          Public service
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: C.faint }}>{formatDate(s.timestamp)}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {s.duration ? formatSeconds(s.duration) : ''}
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ height: 3, background: C.surface2, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    borderRadius: 2,
                    background: isPS ? '#3b82f6' : C.coral,
                  }} />
                </div>
                <div style={{ fontSize: 11, color: C.faint, marginTop: 4, textAlign: 'right' }}>{progress}% gennemført</div>
              </div>
            );
          })}
        </Card>
      ) : (
        <Card>
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎧</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.muted, marginBottom: 6 }}>Ingen lyttehistorik endnu</div>
            <div style={{ fontSize: 13, color: C.faint, marginBottom: 20 }}>Afspil en episode i appen for at komme i gang.</div>
            <a href="/" className="fc-cta-ghost" style={{ display: 'inline-flex', fontSize: 13 }}>
              Gennemse podcasts →
            </a>
          </div>
        </Card>
      )}

      {/* ── Creator CTA ───────────────────────────────────────────── */}
      {!userProfile?.isCreator && (
        <>
          <SectionHeading>Vil du dele din podcast?</SectionHeading>
          <div style={{
            background: C.surface1,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: C.coralDim,
              border: `1px solid rgba(255,107,53,0.3)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, flexShrink: 0,
            }}>🎙️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>Importer din podcast</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                80% revenue share. Ingen binding. Kom i gang på 5 minutter.
              </div>
            </div>
            <a href="/podcast/import" className="fc-cta-primary" style={{ flexShrink: 0, fontSize: 13, padding: '10px 16px' }}>
              Kom i gang →
            </a>
          </div>
        </>
      )}
    </div>
  );
}
